import axios from "axios";

// Ensure backend URL always has /api suffix
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const baseURL = backendUrl ? `${backendUrl}/api` : "/api";

export const http = axios.create({
  baseURL,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    "Pragma": "no-cache"
  },
});

// UI-ONLY MOCK MODE FLAG
const MOCK_MODE = false;

// Attach Authorization header from localStorage on load
const token = localStorage.getItem("token");
if (token && !MOCK_MODE) {
  http.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Add request interceptor
http.interceptors.request.use(
  (config) => {
    if (MOCK_MODE) {
      // In MOCK_MODE, short-circuit ALL requests
      // This is a special trick: we throw a promise or return a rejected promise that 
      // is handled elsewhere, but better yet, we can use an adapter or just return a response.
      console.log(`[HTTP MOCK] Intercepted ${config.method.toUpperCase()} ${config.url}`);

      // Determine what empty data to return based on URL patterns
      let data = {};
      const url = config.url || "";
      if (url.includes('/list') || url.includes('/all') || url.endsWith('s')) {
        data = []; // Likely a list endpoint
      }

      // Short-circuit the request by providing a custom adapter (Axios internal mechanism)
      config.adapter = (config) => {
        return new Promise((resolve) => {
          const response = {
            data: { success: true, ...data }, // Handle both {success: true, data: []} and {[]}
            status: 200,
            statusText: 'OK',
            headers: config.headers,
            config: config,
            request: {}
          };
          // Return actual data if it was just an object as well
          if (Array.isArray(data)) {
            response.data = data;
          }
          resolve(response);
        });
      };
    }

    const token = localStorage.getItem("token");
    if (token && !config.headers["Authorization"] && !MOCK_MODE) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh state — ensures only one refresh call at a time
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// 401 response interceptor — attempt token refresh before logging out
http.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh')
      || requestUrl.includes('/auth/me') || requestUrl.includes('/staff/me') || requestUrl.includes('/staff/login');

    if (error.response?.status === 401 && !isAuthRequest) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No refresh token — logout immediately
        if (window.location.pathname !== '/login') {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          error.config.headers['Authorization'] = `Bearer ${token}`;
          return http(error.config);
        }).catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      return new Promise((resolve, reject) => {
        const userType = localStorage.getItem('userType');
        const refreshUrl = userType === 'staff' ? `${baseURL}/staff/refresh` : `${baseURL}/auth/refresh`;
        axios.post(refreshUrl, { refreshToken })
          .then(({ data }) => {
            const newToken = data.access_token;
            localStorage.setItem('token', newToken);
            http.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            error.config.headers['Authorization'] = `Bearer ${newToken}`;
            processQueue(null, newToken);
            resolve(http(error.config));
          })
          .catch((err) => {
            processQueue(err, null);
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);
